import { useMutation } from '@tanstack/react-query'
import { useMedplum } from '@medplum/react'
import { config } from '@/config'

interface GenerateFollowupInput {
  documentReferenceId: string
  patientId: string
}

interface GenerateFollowupResponse {
  instructionsSubject: string
  instructionsBody: string
  emailSubject: string
  emailBody: string
  patientFirstName: string
  patientEmail?: string
  patientPortalDocumentsUrl?: string
}

interface SaveAndNotifyInput {
  patientId: string
  sourceDocumentReferenceId: string
  instructionsSubject: string
  instructionsBody: string
  emailSubject: string
  emailBody: string
  sendEmail?: boolean
  instructionsPdfBase64?: string
  instructionsPdfFileName?: string
}

interface SaveAndNotifyResponse {
  documentSaved: boolean
  emailSent: boolean
  emailSkipReason?: string
  emailSkipCode?:
    | 'duplicate'
    | 'email_not_requested'
    | 'no_email'
    | 'email_not_configured'
    | 'email_send_failed'
}

function useAuthHeaders() {
  const medplum = useMedplum()
  return () => {
    const token = medplum.getAccessToken()
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
  }
}

export function useFollowupActions() {
  const getHeaders = useAuthHeaders()
  const baseUrl = config.middlewareUrl || ''

  const generate = useMutation({
    mutationFn: async (input: GenerateFollowupInput): Promise<GenerateFollowupResponse> => {
      const res = await fetch(`${baseUrl}/followup/generate`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(input),
      })
      if (!res.ok) {
        const err = await res.text()
        throw new Error(err || 'Failed to generate followup')
      }
      return res.json()
    },
  })

  const saveAndNotify = useMutation({
    mutationFn: async (input: SaveAndNotifyInput): Promise<SaveAndNotifyResponse> => {
      const res = await fetch(`${baseUrl}/followup/save-and-notify`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(input),
      })
      if (!res.ok) {
        const err = await res.text()
        throw new Error(err || 'Failed to save and notify')
      }
      return res.json()
    },
  })

  return { generate, saveAndNotify }
}
